import { useCallback, useLayoutEffect, useRef, type RefObject } from 'react';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ A THREAD FOLLOWS ITS NEWEST LINE ONLY WHILE THE READER IS AT THE     │
 * │ BOTTOM. SCROLLING UP LETS GO; SCROLLING BACK DOWN, OR ASKING, TAKES HOLD. │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `debt.md`, *"From C27.1"*: both agent threads jumped to the end on every streamed delta, which is
 * right while an answer arrives and wrong the moment somebody scrolls up to re-read — every token
 * dragged them back down. One hook for both threads, the full screen's and the dashboard chat's,
 * because they had the same three-line effect with the same defect, and two copies of the fix is
 * the drift this file exists to stop.
 *
 * ⚠️⚠️ **THE DECISION IS MADE WHEN THE READER SCROLLS, NOT WHEN THE CONTENT GROWS.** Asking *"is this
 * near the bottom?"* at growth time gets the wrong answer whenever one delta adds more than the
 * slack — a data card is several hundred pixels — because the bottom has already moved away. So the
 * scroll listener records whether the reader is following, and growth only reads that.
 *
 * ⚠️⚠️ **ANY UPWARD SCROLL LETS GO, HOWEVER SMALL — THE SLACK ONLY DECIDES TAKING HOLD AGAIN.**
 * A trackpad sends a scroll as a run of small deltas, and the first few land inside any sensible
 * slack. Were *"near the bottom keeps following"* checked first, a delta arriving in that window
 * would jump to the end and swallow the gesture, and a gentle scroll up during a fast reply would
 * never get off the bottom. So moving up is checked first. ⚠️ Headless Chrome cannot show the
 * difference — its synthetic wheel moves a whole 100px notch at once, past the slack, and both
 * orderings let go there — so this is argued, not measured.
 *
 * ⚠️ **And only an upward scroll lets go**, because a scroll event also fires after our own jump, and
 * if content grew in between without a commit of ours (an avatar loading into a card) the distance
 * exceeds the slack although nobody moved. Growth never lowers `scrollTop`; a person scrolling up
 * always does. ⚠️ The one thing that lowers it without a person is the content SHRINKING — a failed
 * turn's half-reply discarded — and the browser then clamps to the new bottom, distance 0, which is
 * why *"moved up"* also requires being off the bottom.
 *
 * ⚠️ **A layout effect with no dependency list, deliberately.** It runs after every commit of the
 * thread, before paint, and a commit is exactly when its content may have grown — text, a tool row,
 * reasoning, a card, a proposal. A dependency list would have to name every one of those, and the
 * list it replaced named only the text: a tool row or an open reasoning disclosure grew below the
 * fold unfollowed. Before paint, so a following reader never sees one frame of the new line
 * hanging below the edge. The read of `scrollHeight` costs a layout the commit was about to pay
 * for anyway.
 *
 * ⚠️ **`scrollTop` on the scroller, not `scrollIntoView` on a sentinel.** `scrollIntoView` scrolls
 * every scrollable ancestor too — the page, a dashboard panel — and with `block: 'end'` it stopped
 * at the sentinel and left the scroller's bottom padding below the fold, so a thread at rest sat
 * that padding away from its own bottom.
 */

/**
 * How close to the bottom counts as *at* the bottom, in CSS pixels.
 *
 * ⚠️ **32, because it is the full thread's own bottom padding (`py-8`)**: inside it, the last line
 * of content is wholly on screen, which is what *"at the bottom"* means to somebody reading. It also
 * absorbs the fractional `scrollTop` a zoomed page reports. It governs only TAKING HOLD — scrolling
 * back down to within it resumes following; letting go is any upward movement (above).
 */
export const FOLLOW_SLACK_PX = 32;

export interface FollowThread {
  /** For the scrolling element. */
  readonly ref: RefObject<HTMLDivElement | null>;
  /** For the same element's `onScroll`. */
  readonly onScroll: () => void;
}

/**
 * @param holdKey Changes when the reader should be taken back to the bottom regardless of where
 *   they are: a different thread opened, or a question just sent. ⚠️ Pass the newest **question's**
 *   id — it changes on both, and not when the reply's draft id is swapped for the stored one at
 *   `message_stop`, which would snap somebody back down at the end of every answer.
 */
export function useFollowThread(holdKey: string | null): FollowThread {
  const ref = useRef<HTMLDivElement | null>(null);
  const following = useRef(true);
  const lastTop = useRef(0);

  const onScroll = useCallback(() => {
    const element = ref.current;
    if (element === null) return;
    const top = element.scrollTop;
    const distance = element.scrollHeight - top - element.clientHeight;
    if (top < lastTop.current && distance > 1) following.current = false;
    else if (distance <= FOLLOW_SLACK_PX) following.current = true;
    lastTop.current = top;
  }, []);

  /* ⚠️ Declared BEFORE the follow below, because layout effects run in declaration order: a new
   * question takes hold first, and the same commit then jumps. */
  useLayoutEffect(() => {
    following.current = true;
  }, [holdKey]);

  useLayoutEffect(() => {
    const element = ref.current;
    if (element === null || !following.current) return;
    element.scrollTop = element.scrollHeight;
    lastTop.current = element.scrollTop;
  });

  return { ref, onScroll };
}
